import { useMutation, useQuery } from '@tanstack/react-query';
import queryString from 'query-string';

import {
  RecordsWithTimestamp,
  SEARCH_OPTION,
  SearchHistoryEntry,
  useUrlManager,
} from '@app/components';
import { usePatronSession } from '@app/hooks';
import { makeRequest } from '@app/utils';

const recentActivityLimit = 200;

export interface RecentActivityInstanceDto {
  id: string;
  instanceId: string;
  metadata: { createdDate: string };
}

export type RecentActivityInstancesDto = RecentActivityInstanceDto[];

export interface UserSearchDto {
  query: string;
  requestParams: string;
  resultCount: number;
  date: string;
  interface: string;
  searchType: SEARCH_OPTION;
  id?: string;
}

const createInstancesUrl = (externalSystemId: string) =>
  `/opac-patron/account/${externalSystemId}/recent-instance-activities`;

const createSearchesUrl = (externalSystemId: string) =>
  `/opac-patron/account/${externalSystemId}/recent-search-activities`;

export const useRecentActivityInstancesFetch = () => {
  const { externalSystemId } = usePatronSession();
  const url = createInstancesUrl(externalSystemId as string);

  return useQuery<RecentActivityInstancesDto, unknown, RecordsWithTimestamp[]>(
    ['recentActivityInstances', externalSystemId],
    () =>
      makeRequest<RecentActivityInstancesDto>({
        url,
        params: {
          limit: recentActivityLimit,
        },
      }),
    {
      enabled: Boolean(externalSystemId),
      select: instances =>
        instances.map(({ instanceId, metadata, id }) => ({
          instanceId,
          timestamp: metadata.createdDate,
          id,
        })),
    }
  );
};

interface UseSaveRecentInstanceMutationProps {
  viewedInstances: { instanceId: string }[];
  systemId?: string;
}

export const useSaveRecentInstancesMutation = () => {
  const { externalSystemId = '' } = usePatronSession();

  return useMutation(
    ({ viewedInstances, systemId = '' }: UseSaveRecentInstanceMutationProps) =>
      makeRequest({
        url: createInstancesUrl(systemId || externalSystemId),
        method: 'POST',
        data: viewedInstances,
      })
  );
};

export const useFormattedSearches = () => {
  const { getSearchStateFromUrl } = useUrlManager();

  return (searches: UserSearchDto[]): SearchHistoryEntry[] =>
    searches.map(
      ({
        requestParams,
        resultCount: totalRecords,
        date: timestamp,
        interface: searchInterface,
        id,
        query,
        searchType,
      }) => ({
        searchInterface,
        timestamp,
        requestParams,
        totalRecords,
        id,
        query,
        searchType,
        ...getSearchStateFromUrl(queryString.parse(requestParams)),
      })
    );
};

export const useRecentSearchesFetch = () => {
  const { externalSystemId } = usePatronSession();
  const url = createSearchesUrl(externalSystemId as string);
  const formatSearches = useFormattedSearches();

  return useQuery<UserSearchDto[], unknown, SearchHistoryEntry[]>(
    ['recentActivitySearches', externalSystemId],
    () =>
      makeRequest<UserSearchDto[]>({
        url,
        params: {
          limit: recentActivityLimit,
        },
      }),
    {
      select: formatSearches,
      enabled: Boolean(externalSystemId),
    }
  );
};

interface UseSaveRecentSearchesMutationProps {
  searches: UserSearchDto[];
  systemId?: string;
}

export const useSaveRecentSearchesMutation = () => {
  const { externalSystemId } = usePatronSession();

  return useMutation(
    ({ searches, systemId }: UseSaveRecentSearchesMutationProps) =>
      makeRequest({
        url: createSearchesUrl(systemId || (externalSystemId as string)),
        method: 'POST',
        data: searches,
      })
  );
};

export const useRecentSearchesRemoval = () => {
  const { externalSystemId = '' } = usePatronSession();

  return useMutation((searchIds: string[]) =>
    makeRequest({
      url: createSearchesUrl(externalSystemId),
      method: 'DELETE',
      data: {
        params: { searchIds: searchIds.join(',') },
      },
    })
  );
};

export const useRecentInstancesRemoval = () => {
  const { externalSystemId = '' } = usePatronSession();

  return useMutation((recentInstanceActivityIds: string[]) =>
    makeRequest({
      url: createInstancesUrl(externalSystemId),
      method: 'DELETE',
      data: {
        params: {
          recentInstanceActivityIds: recentInstanceActivityIds.join(','),
        },
      },
    })
  );
};
